import { useState, useEffect } from "react";
import { ArrowLeft, CalendarCheck, Pencil, Check, Sparkles } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useNavigate, useParams } from "react-router";
import { Textarea } from "../../../components/ui/textarea";
import { CustomTimePicker } from "../../../components/custom-time-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { toast, ToastContainer } from "react-toastify";
import {
  getQuoteRequest,
  updateQuoteRequest,
  convertQuoteToBooking,
} from "../../../services/api";
import { cn } from "../../../lib/util";

interface GlamPackage {
  id: string;
  title: string;
  price: number;
  description: string;
  image_path: string;
  created_at: string;
  updated_at: string;
}

interface QuoteRequest {
  id: string;
  name: string;
  email_address: string;
  phone_number: string;
  glam_package_id: string;
  glam_spot: string;
  number_of_faces: string;
  booking_start_date: string;
  booking_end_date: string;
  booking_start_date_time: string;
  booking_end_date_time: string;
  home_address: string | null;
  landmark: string | null;
  additional_info: string | null;
  google_calendar_link: string | null;
  booking_name: string;
  created_at: string;
  updated_at: string;
  glam_package: GlamPackage;
}

const ctrl =
  "w-full rounded-xl border border-[#E3C9C8] bg-white px-4 py-3 text-[15px] text-[#3a2e2c] " +
  "placeholder:text-[#bcaca9] outline-none transition-colors " +
  "focus-visible:ring-2 focus-visible:ring-[#B87775]/50 focus-visible:border-[#B87775] " +
  "disabled:bg-[#FBEFEF]/30 disabled:text-[#7c6c6a] disabled:cursor-default";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-[13px] font-medium text-[#8a5a58]">{label}</label>
      {children}
    </div>
  );
}

export function QuoteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<QuoteRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [converting, setConverting] = useState(false);

  const handleInputChange = (field: keyof QuoteRequest, value: any) => {
    setFormData((prev) => ({ ...prev!, [field]: value }));
  };

  const handleBack = () => navigate("/control/quotes");

  const handleEditToggle = () => {
    if (isEditing && formData) handleUpdateQuote(formData);
    setIsEditing(!isEditing);
  };

  const handleConvertToBooking = async () => {
    if (!formData?.id) return;
    setConverting(true);
    try {
      await convertQuoteToBooking(formData.id);
      toast.success("Quote converted to a booking.");
      setConfirmOpen(false);
      navigate("/control/bookings");
    } catch (error) {
      console.error("Error converting quote:", error);
      toast.error("Failed to convert quote to booking");
    } finally {
      setConverting(false);
    }
  };

  const handleUpdateQuote = async (data: Partial<QuoteRequest>) => {
    if (!formData?.id) return;
    try {
      await updateQuoteRequest(formData.id, data);
      toast.success("Quote updated.");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating quote:", error);
      toast.error("Failed to update quote");
    }
  };

  const formatDateTimeForApi = (date: string, time: string) => `${date} ${time}:00`;

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setStartDate(newDate);
    if (formData) {
      const dt = formatDateTimeForApi(newDate, startTime);
      setFormData({
        ...formData,
        booking_start_date: newDate,
        booking_end_date: newDate,
        booking_start_date_time: dt,
        booking_end_date_time: dt,
      });
    }
  };

  const handleTimeSelect = (newTime: string) => {
    setStartTime(newTime);
    if (formData) {
      const dt = formatDateTimeForApi(startDate, newTime);
      setFormData({ ...formData, booking_start_date_time: dt, booking_end_date_time: dt });
    }
  };

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        setIsLoading(true);
        const response = (await getQuoteRequest(id as string)) as any;
        const apiResponse = response.data.data;
        const [, existingTime] = apiResponse.booking_start_date_time.split(" ");
        setFormData(apiResponse);
        setStartDate(apiResponse.booking_start_date);
        setStartTime((existingTime ?? "").substring(0, 5));
      } catch (error) {
        console.error("Error fetching quote:", error);
        toast.error("Failed to load quote details");
      } finally {
        setIsLoading(false);
      }
    };
    if (id) fetchQuote();
  }, [id]);

  const formatDateTime = (dateTimeStr: string) => {
    try {
      return format(parseISO(dateTimeStr.replace(" ", "T")), "EEE, d MMM yyyy · h:mm a");
    } catch {
      return dateTimeStr;
    }
  };

  const getGlamSpotLabel = (glamSpot: string) =>
    glamSpot === "studio_session" ? "Studio session" : "Home service";

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 py-20 text-[#9A6B69]">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#EAD7D6] border-t-[#B87775]" />
        Loading quote…
      </div>
    );
  }

  if (!formData) {
    return <div className="py-20 text-center text-[#8a7a78]">Quote not found.</div>;
  }

  return (
    <div className="mx-auto max-w-3xl">
      <ToastContainer />

      {/* Header */}
      <button
        type="button"
        onClick={handleBack}
        className="mb-4 inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-sm text-[#B87775] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to quote requests
      </button>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#B87775]">Quote request</p>
          <h1 className="font-serif text-3xl text-[#3a2e2c]">{formData.name}</h1>
          <p className="mt-1 text-sm text-[#8a7a78]">
            {formData.glam_package?.title || "Glam session"} · requested{" "}
            {formatDateTime(formData.booking_start_date_time)}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleEditToggle}
            className="inline-flex h-11 items-center gap-2 rounded-full border border-[#E3C9C8] px-4 text-sm font-medium text-[#B87775] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
          >
            {isEditing ? <Check className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            {isEditing ? "Save changes" : "Edit"}
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="inline-flex h-11 items-center gap-2 rounded-full bg-[#B87775] px-5 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
          >
            <CalendarCheck className="h-4 w-4" />
            Convert to booking
          </button>
        </div>
      </div>

      {/* Details */}
      <div className="rounded-2xl border border-[#EAD7D6] bg-white p-6">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Full name">
            <input
              className={ctrl}
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              disabled={!isEditing}
            />
          </Field>
          <Field label="Email address">
            <input
              type="email"
              className={ctrl}
              value={formData.email_address}
              onChange={(e) => handleInputChange("email_address", e.target.value)}
              disabled={!isEditing}
            />
          </Field>
          <Field label="Phone number">
            <input
              className={ctrl}
              value={formData.phone_number}
              onChange={(e) => handleInputChange("phone_number", e.target.value)}
              disabled={!isEditing}
            />
          </Field>
          <Field label="Glam package">
            <input className={ctrl} value={formData.glam_package?.title || "Unknown package"} disabled />
          </Field>
          <Field label="Glam spot">
            <input className={ctrl} value={getGlamSpotLabel(formData.glam_spot)} disabled />
          </Field>
          <Field label="Number of faces">
            <input
              type="number"
              className={ctrl}
              value={formData.number_of_faces}
              onChange={(e) => handleInputChange("number_of_faces", e.target.value)}
              disabled={!isEditing}
            />
          </Field>

          {formData.glam_spot === "home_service" && (
            <>
              <Field label="Home address">
                <input
                  className={ctrl}
                  value={formData.home_address || ""}
                  onChange={(e) => handleInputChange("home_address", e.target.value)}
                  disabled={!isEditing}
                />
              </Field>
              {formData.landmark && (
                <Field label="Landmark">
                  <input
                    className={ctrl}
                    value={formData.landmark}
                    onChange={(e) => handleInputChange("landmark", e.target.value)}
                    disabled={!isEditing}
                  />
                </Field>
              )}
            </>
          )}

          <Field label="Glam date">
            <input
              type="date"
              className={cn(ctrl, "tabular-nums")}
              value={startDate}
              onChange={handleDateChange}
              disabled={!isEditing}
            />
          </Field>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[#8a5a58]">
              Glam time{" "}
              <span className="font-normal text-[#9A6B69]">· Atlantic Time</span>
            </label>
            <CustomTimePicker
              selectedTime={startTime}
              onTimeSelect={handleTimeSelect}
              isDisabled={!isEditing}
            />
          </div>
        </div>

        {(formData.additional_info || isEditing) && (
          <div className="mt-5">
            <label className="mb-1.5 block text-[13px] font-medium text-[#8a5a58]">
              Additional information
            </label>
            <Textarea
              className={cn(ctrl, "min-h-[110px] resize-y")}
              value={formData.additional_info || ""}
              onChange={(e) => handleInputChange("additional_info", e.target.value)}
              disabled={!isEditing}
              placeholder="Notes from the client…"
            />
          </div>
        )}
      </div>

      {/* Convert confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md rounded-2xl border-[#EAD7D6]">
          <DialogHeader>
            <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-[#FBEFEF]">
              <Sparkles className="h-5 w-5 text-[#B87775]" />
            </div>
            <DialogTitle className="font-serif text-2xl font-normal text-[#3a2e2c]">
              Convert to a booking?
            </DialogTitle>
            <DialogDescription className="text-[#8a7a78]">
              This creates a confirmed booking for{" "}
              <span className="font-medium text-[#3a2e2c]">{formData.name}</span> and removes it
              from quote requests.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-[#EAD7D6] bg-[#FBEFEF]/40 p-4 text-sm">
            <div className="flex justify-between py-1">
              <span className="text-[#9A6B69]">Package</span>
              <span className="font-medium text-[#3a2e2c]">
                {formData.glam_package?.title || "—"}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-[#9A6B69]">When</span>
              <span className="tabular-nums font-medium text-[#3a2e2c]">
                {formatDateTime(formData.booking_start_date_time)}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-[#9A6B69]">Spot</span>
              <span className="font-medium text-[#3a2e2c]">
                {getGlamSpotLabel(formData.glam_spot)}
              </span>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="inline-flex h-11 items-center justify-center rounded-full border border-[#E3C9C8] px-5 text-sm font-medium text-[#8a5a58] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConvertToBooking}
              disabled={converting}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#B87775] px-6 text-sm font-medium text-white transition hover:bg-[#a96764] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
            >
              {converting ? "Converting…" : "Convert to booking"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
